import { entryKeys, merchantKeys, type Merchant, type MerchantMergeResult } from '@taler/api-client'
import { useQueryClient } from '@tanstack/vue-query'
import { useApi } from '@/app/api'
import { countMerchantEntries } from '../internal/entry-count.ts'

export type { Merchant, MerchantMergeResult }

export interface UseMerchantActionsOptions {
  readonly ledgerId: () => string | undefined
}

export interface UseMerchantActions {
  addAlias(merchantId: string, alias: string): Promise<Merchant>
  removeAlias(merchantId: string, alias: string): Promise<Merchant>
  /** How many entries a merge of `sourceId` into some destination would move, so the merge
   * dialog can state its consequence before the user confirms it. */
  countEntries(sourceId: string, signal?: AbortSignal): Promise<number>
  merge(sourceId: string, destinationId: string): Promise<MerchantMergeResult>
}

export function useMerchantActions(options: UseMerchantActionsOptions): UseMerchantActions {
  const api = useApi()
  const queryClient = useQueryClient()

  async function invalidate(): Promise<void> {
    const ledgerId = options.ledgerId()
    if (ledgerId === undefined) return
    for (const key of merchantKeys.invalidatedByWrite(ledgerId)) {
      await queryClient.invalidateQueries({ queryKey: key })
    }
  }

  return {
    async addAlias(merchantId, alias) {
      const result = await api.merchants.addAlias(options.ledgerId()!, merchantId, alias)
      await invalidate()
      return result
    },
    async removeAlias(merchantId, alias) {
      const result = await api.merchants.removeAlias(options.ledgerId()!, merchantId, alias)
      await invalidate()
      return result
    },
    countEntries(sourceId, signal) {
      return countMerchantEntries(api, options.ledgerId()!, sourceId, signal)
    },
    async merge(sourceId, destinationId) {
      const result = await api.merchants.merge(options.ledgerId()!, sourceId, destinationId)
      await invalidate()
      for (const key of entryKeys.invalidatedByWrite(options.ledgerId()!)) {
        await queryClient.invalidateQueries({ queryKey: key })
      }
      return result
    },
  }
}
